import { Extension, type Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection, type EditorState } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { SKIPPED_BLOCKS, SKIPPED_MARKS } from './spell-tokenize';

/**
 * AI spelling and grammar suggestions, requested by the person from Review → Proofing. Suggestions are
 * view-only decorations (never saved, shared or tracked) and change the document only when the person
 * accepts one. Results that arrive after the paragraph changed are discarded.
 */
export const AI_PROOFING_MAX_PARAGRAPHS = 8;
export const AI_PROOFING_MAX_PARAGRAPH_CHARS = 2_000;
/** Paragraphs checked per request from the person (three server calls at most). */
export const AI_PROOFING_MAX_PARAGRAPHS_PER_CHECK = 24;

export type AiProofingKind = 'spelling' | 'grammar';
export type AiProofingConfidence = 'obvious' | 'likely' | 'unsure';

/** One issue returned by the server, at offsets into the paragraph text that was sent. */
export interface AiProofingResultIssue {
  paragraphId: string;
  start: number;
  end: number;
  original: string;
  replacement: string;
  kind: AiProofingKind;
  confidence: AiProofingConfidence;
  reason: string;
}

/** A suggestion currently shown in the document. */
export interface AiProofingSuggestion {
  id: string;
  from: number;
  to: number;
  original: string;
  /** Empty means "remove this text". */
  replacement: string;
  kind: AiProofingKind;
  confidence: AiProofingConfidence;
  reason: string;
}

/** A paragraph sent for checking: where it started and the exact text sent. */
export interface AiProofingParagraph { id: string; pos: number; text: string }

type SuggestionSpec = Omit<AiProofingSuggestion, 'from' | 'to'>;

type AiProofingMeta =
  | { type: 'track'; paragraphs: AiProofingParagraph[] }
  | { type: 'results'; paragraphIds: string[]; issues: AiProofingResultIssue[] }
  | { type: 'untrack'; paragraphIds: string[] }
  | { type: 'dismiss'; id: string }
  | { type: 'clear' };

interface AiProofingState {
  decorations: DecorationSet;
  /** Paragraphs waiting for a result, mapped through every change since they were sent. */
  pending: readonly AiProofingParagraph[];
}

export const aiProofingKey = new PluginKey<AiProofingState>('documentAiProofing');
const SPEC_KEY = 'aiProofing';
let nextSuggestion = 0;

/** One character per document position (code, links and tracked deletions blanked) so offsets map to positions. */
export function aiProofingBlockText(block: ProseMirrorNode): string {
  if (SKIPPED_BLOCKS.has(block.type.name) || block.type.spec.code) return '';
  let text = '';
  block.forEach((child) => {
    if (child.isText && child.text) {
      const skipped = child.marks.some((mark) => SKIPPED_MARKS.has(mark.type.name) || mark.type.spec.code);
      text += skipped ? ' '.repeat(child.text.length) : child.text;
    } else {
      text += ' '.repeat(child.nodeSize);
    }
  });
  return text;
}

function eligible(block: ProseMirrorNode, text: string): boolean {
  return text.length === block.content.size && text.length <= AI_PROOFING_MAX_PARAGRAPH_CHARS
    && text.trim().length >= 3 && /\p{L}{2,}/u.test(text);
}

export interface AiProofingSelection {
  paragraphs: AiProofingParagraph[];
  /** Eligible paragraphs remain after the checked ones. */
  more: boolean;
  /** Paragraphs longer than the per-paragraph limit, which are not checked. */
  skippedLong: number;
}

/**
 * The paragraphs to check: those in the selection, or from the paragraph with the cursor to the end of
 * the document, bounded per request.
 */
export function collectAiProofingParagraphs(state: EditorState, runId: string, limit = AI_PROOFING_MAX_PARAGRAPHS_PER_CHECK): AiProofingSelection {
  const { selection, doc } = state;
  const from = selection.empty ? selection.$from.start(Math.min(selection.$from.depth, 1)) : selection.from;
  const to = selection.empty ? doc.content.size : selection.to;
  const paragraphs: AiProofingParagraph[] = [];
  let more = false;
  let skippedLong = 0;
  doc.nodesBetween(Math.max(0, from - 1), to, (node, pos) => {
    if (more) return false;
    if (!node.isTextblock) return true;
    const text = aiProofingBlockText(node);
    if (text.length > AI_PROOFING_MAX_PARAGRAPH_CHARS && text.trim()) { skippedLong += 1; return false; }
    if (!eligible(node, text)) return false;
    if (paragraphs.length >= limit) { more = true; return false; }
    paragraphs.push({ id: `${runId}p${paragraphs.length}`, pos, text });
    return false;
  });
  return { paragraphs, more, skippedLong };
}

function decorationFor(suggestion: AiProofingSuggestion): Decoration {
  const { from, to, ...spec } = suggestion;
  return Decoration.inline(from, to, {
    class: 'ai-proofing-issue',
    'data-ai-proofing-kind': suggestion.kind,
    title: suggestion.replacement ? `${suggestion.reason} Suggestion: ${suggestion.replacement}` : `${suggestion.reason} Suggestion: remove`
  }, { [SPEC_KEY]: spec });
}

function toSuggestion(decoration: Decoration): AiProofingSuggestion {
  return { ...(decoration.spec[SPEC_KEY] as SuggestionSpec), from: decoration.from, to: decoration.to };
}

function stillMatches(doc: ProseMirrorNode, decoration: Decoration): boolean {
  const spec = decoration.spec[SPEC_KEY] as SuggestionSpec | undefined;
  return Boolean(spec) && decoration.to <= doc.content.size && doc.textBetween(decoration.from, decoration.to) === spec?.original;
}

function addResults(doc: ProseMirrorNode, value: AiProofingState, meta: Extract<AiProofingMeta, { type: 'results' }>): AiProofingState {
  const ids = new Set(meta.paragraphIds);
  let { decorations } = value;
  const added: Decoration[] = [];
  for (const paragraph of value.pending) {
    if (!ids.has(paragraph.id)) continue;
    const node = doc.nodeAt(paragraph.pos);
    // The paragraph changed while it was being checked: its result is stale.
    if (!node?.isTextblock || aiProofingBlockText(node) !== paragraph.text) continue;
    const contentStart = paragraph.pos + 1;
    const blockEnd = contentStart + node.content.size;
    decorations = decorations.remove(decorations.find(contentStart, blockEnd, (spec) => Boolean(spec[SPEC_KEY])));
    for (const issue of meta.issues) {
      if (issue.paragraphId !== paragraph.id || issue.end <= issue.start || issue.end > paragraph.text.length) continue;
      if (paragraph.text.slice(issue.start, issue.end) !== issue.original || issue.replacement === issue.original) continue;
      const from = contentStart + issue.start;
      const to = contentStart + issue.end;
      if (doc.textBetween(from, to) !== issue.original) continue;
      nextSuggestion += 1;
      added.push(decorationFor({
        id: `ai${nextSuggestion}`, from, to, original: issue.original, replacement: issue.replacement,
        kind: issue.kind, confidence: issue.confidence, reason: issue.reason
      }));
    }
  }
  if (added.length) decorations = decorations.add(doc, added);
  return { decorations, pending: value.pending.filter((paragraph) => !ids.has(paragraph.id)) };
}

export function createAiProofingPlugin(): Plugin<AiProofingState> {
  return new Plugin<AiProofingState>({
    key: aiProofingKey,
    state: {
      init: () => ({ decorations: DecorationSet.empty, pending: [] }),
      apply(tr, value) {
        const meta = tr.getMeta(aiProofingKey) as AiProofingMeta | undefined;
        let decorations = value.decorations.map(tr.mapping, tr.doc);
        let pending = value.pending;
        if (tr.docChanged) {
          const stale = decorations.find(undefined, undefined, (spec) => Boolean(spec[SPEC_KEY])).filter((item) => !stillMatches(tr.doc, item));
          if (stale.length) decorations = decorations.remove(stale);
          pending = pending.flatMap((paragraph) => {
            const mapped = tr.mapping.mapResult(paragraph.pos, 1);
            return mapped.deleted ? [] : [{ ...paragraph, pos: mapped.pos }];
          });
        }
        let next: AiProofingState = { decorations, pending };
        if (!meta) return next;
        if (meta.type === 'track') next = { ...next, pending: [...next.pending, ...meta.paragraphs] };
        else if (meta.type === 'untrack') next = { ...next, pending: next.pending.filter((paragraph) => !meta.paragraphIds.includes(paragraph.id)) };
        else if (meta.type === 'results') next = addResults(tr.doc, next, meta);
        else if (meta.type === 'dismiss') {
          next = { ...next, decorations: next.decorations.remove(next.decorations.find(undefined, undefined, (spec) => (spec[SPEC_KEY] as SuggestionSpec | undefined)?.id === meta.id)) };
        } else if (meta.type === 'clear') next = { decorations: DecorationSet.empty, pending: [] };
        return next;
      }
    },
    props: {
      decorations: (state) => aiProofingKey.getState(state)?.decorations ?? null
    }
  });
}

export const DocumentAiProofing = Extension.create({
  name: 'documentAiProofing',
  addProseMirrorPlugins() { return [createAiProofingPlugin()]; }
});

function dispatch(editor: Editor, meta: AiProofingMeta): void {
  if (editor.isDestroyed || !aiProofingKey.getState(editor.state)) return;
  editor.view.dispatch(editor.state.tr.setMeta(aiProofingKey, meta).setMeta('addToHistory', false));
}

/** Suggestions currently shown, in document order. */
export function aiProofingSuggestions(state: EditorState): AiProofingSuggestion[] {
  const value = aiProofingKey.getState(state);
  if (!value) return [];
  return value.decorations.find(undefined, undefined, (spec) => Boolean(spec[SPEC_KEY])).map(toSuggestion).sort((left, right) => left.from - right.from);
}

/** The suggestion at a document position, if any. */
export function aiProofingSuggestionAt(state: EditorState, position: number): AiProofingSuggestion | null {
  const items = aiProofingSuggestions(state);
  return items.find((item) => item.from <= position && position < item.to)
    ?? items.find((item) => item.from <= position && position <= item.to)
    ?? null;
}

export function trackAiProofingParagraphs(editor: Editor, paragraphs: AiProofingParagraph[]): void { dispatch(editor, { type: 'track', paragraphs }); }
export function untrackAiProofingParagraphs(editor: Editor, paragraphIds: string[]): void { dispatch(editor, { type: 'untrack', paragraphIds }); }
export function showAiProofingResults(editor: Editor, paragraphIds: string[], issues: AiProofingResultIssue[]): void {
  dispatch(editor, { type: 'results', paragraphIds, issues });
}
export function dismissAiProofingSuggestion(editor: Editor, id: string): void { dispatch(editor, { type: 'dismiss', id }); }
export function clearAiProofing(editor: Editor): void { dispatch(editor, { type: 'clear' }); }

/**
 * Applies one suggestion as an ordinary, undoable edit if its text is still exactly where it was shown.
 * Returns false (and changes nothing) when the text has changed since.
 */
export function acceptAiProofingSuggestion(editor: Editor, id: string): boolean {
  if (editor.isDestroyed || !editor.isEditable) return false;
  const suggestion = aiProofingSuggestions(editor.state).find((item) => item.id === id);
  if (!suggestion || editor.state.doc.textBetween(suggestion.from, suggestion.to) !== suggestion.original) return false;
  const tr = editor.state.tr;
  if (suggestion.replacement) tr.insertText(suggestion.replacement, suggestion.from, suggestion.to);
  else tr.delete(suggestion.from, suggestion.to);
  tr.setSelection(TextSelection.create(tr.doc, suggestion.from + suggestion.replacement.length));
  tr.setMeta(aiProofingKey, { type: 'dismiss', id } satisfies AiProofingMeta);
  editor.view.dispatch(tr);
  editor.view.focus();
  return true;
}

/** Selects a suggestion's text so the person can see it in context. */
export function revealAiProofingSuggestion(editor: Editor, id: string): void {
  const suggestion = aiProofingSuggestions(editor.state).find((item) => item.id === id);
  if (!suggestion || editor.isDestroyed) return;
  editor.chain().focus().setTextSelection({ from: suggestion.from, to: suggestion.to }).scrollIntoView().run();
}
